import { target } from '@github/catalyst';
import { dispatchChange } from '../util';
import {
  AnnotationEditorUIManager,
  PDFDocumentProxy,
  PDFViewer,
  PDFViewerApplication,
  PDFViewerWindow
} from './pdfjs';

const annotationFieldSelector =
  'section[data-annotation-id] [data-element-id]:is(input, select, textarea)';

export abstract class PDFViewerBase extends HTMLElement {
  static nextInstance = 0;
  @target declare iframe: HTMLIFrameElement;
  pdfLoaded = Promise.withResolvers<PDFViewerApplication>();
  viewerApplication: PDFViewerApplication | undefined;
  annotationEditorUIManager: AnnotationEditorUIManager | undefined;
  detachedController: AbortController | undefined;
  viewerOptionsConfigured = false;
  instance = PDFViewerBase.nextInstance++;

  abstract onAnnotationFieldClick?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onAnnotationFieldFocus?(
    viewerApplication: PDFViewerApplication,
    target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    event: Event
  ): void;

  abstract onAnnotationFieldChange?(
    viewerApplication: PDFViewerApplication,
    id: string,
    element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  ): void;

  abstract onCtrlS?(viewerApplication: PDFViewerApplication): void;

  abstract onAnnotationEditorLayerRendered?(
    viewerApplication: PDFViewerApplication
  ): void;

  abstract onAnnotationEditorUiManager?(args: {
    source: PDFViewer;
    uiManager: AnnotationEditorUIManager;
  }): void;

  abstract onAnnotationEditorModeChanged?(
    viewerApplication: PDFViewerApplication
  ): void;

  abstract onPdfDocumentLoaded?(pdfDocument: PDFDocumentProxy): Promise<void>;

  abstract onPdfLoaded?(viewerApplication: PDFViewerApplication): void;

  connectedCallback() {
    this.detachedController = new AbortController();
    this.ownerDocument.addEventListener(
      'webviewerloaded',
      this.handleWebViewerLoaded,
      { signal: this.detachedController.signal }
    );
  }

  disconnectedCallback() {
    this.detachedController?.abort();
  }

  handleWebViewerLoaded = (event: Event) => {
    const source = (event as CustomEvent<{ source?: PDFViewerWindow }>).detail
      ?.source;
    if (source !== this.iframe.contentWindow) {
      return;
    }

    // PDF.js dispatches webviewerloaded before initializing the viewer so
    // embedders can configure AppOptions before preferences and viewer
    // components are created.
    const options = source.PDFViewerApplicationOptions;
    options.set('disablePreferences', true);
    options.set('defaultUrl', '');
    options.set('enableSignatureEditor', false);

    if (!this.hasAttribute('restore-position')) {
      options.set('viewOnLoad', 1); // 1 = initial position
      options.set('defaultZoomValue', 'auto');
      options.set('sidebarViewOnLoad', 0); // 0 = none
      options.set('scrollModeOnLoad', 0); // 0 = vertical scrolling
      options.set('spreadModeOnLoad', 0); // 0 = no spreads
    }
    this.viewerOptionsConfigured = true;
  };

  async iframeLoaded() {
    this.log('IFrame loaded');
    const iframe = this.iframe;
    const viewerWindow = iframe.contentWindow;
    if (!viewerWindow) {
      throw new Error('Expected iframe contentWindow');
    }
    if (!this.viewerOptionsConfigured) {
      throw new Error(
        'Expected PDF viewer options to be configured before initialization'
      );
    }

    viewerWindow.document.documentElement.toggleAttribute(
      'cumulus--hide-tools',
      true
    );

    const viewerApplication = (this.viewerApplication = (viewerWindow as any)
      .PDFViewerApplication as PDFViewerApplication);
    const pdfFile =
      iframe.getAttribute('data-pdf-file') ??
      new URL(viewerWindow.location.href).searchParams.get('file');

    // Prevent edits to PDF from interrupting beforeunload event.
    viewerApplication._hasChanges = () => false;

    // intercept load
    this.log('Intercepting PDF load');
    const standardLoad = viewerApplication.load;
    const interceptedLoad = Promise.withResolvers<void>();

    viewerApplication.load = pdfDocument => {
      this.log('PDF load intercepted');
      viewerApplication.load = standardLoad;

      void (async () => {
        try {
          // Intercept setValue
          const standardSetValue = pdfDocument.annotationStorage.setValue;
          pdfDocument.annotationStorage.setValue = (id, value) => {
            this.log('annotationStorage.setValue', id, value);
            standardSetValue.call(pdfDocument.annotationStorage, id, value);
            const el = viewerWindow.document.querySelector(
              `[data-element-id="${id}"]`
            );
            if (this.isAnnotationField(el)) {
              this.onAnnotationFieldChange?.(viewerApplication, id, el);
            }
          };

          await this.onPdfDocumentLoaded?.(pdfDocument);
          standardLoad.call(viewerApplication, pdfDocument);
          interceptedLoad.resolve();
        } catch (error) {
          interceptedLoad.reject(error);
        }
      })();
    };

    viewerApplication.eventBus.on(
      'annotationeditoruimanager',
      args => {
        this.annotationEditorUIManager = args.uiManager;
        this.log('AnnotationEditorUIManager ready');
        this.onAnnotationEditorUiManager?.(args);
      },
      {
        signal: this.detachedController?.signal
      }
    );

    viewerApplication.eventBus.on(
      'annotationeditorlayerrendered',
      () => {
        this.log('Annotation editor layer rendered');
        this.onAnnotationEditorLayerRendered?.(viewerApplication);
      },
      {
        signal: this.detachedController?.signal
      }
    );

    if (this.hasAttribute('readonly')) {
      viewerApplication.eventBus.on(
        'annotationlayerrendered',
        () => {
          this.iframe.contentDocument
            ?.querySelectorAll(annotationFieldSelector)
            .forEach(el => {
              if (this.isAnnotationField(el)) {
                el.disabled = true;
              }
            });
        },
        {
          signal: this.detachedController?.signal
        }
      );
    }

    await this.viewerApplication.initializedPromise;
    this.log('Viewer application initialized');

    if (pdfFile) {
      this.log('Opening PDF file', pdfFile);
      await viewerApplication.open({ url: pdfFile });
      await interceptedLoad.promise;
    }

    await this.viewerApplication.pdfLoadingTask?.promise;
    this.log('Loading task resolved');

    // Prevent drop from updating the PDF document.
    viewerApplication.appConfig.mainContainer.addEventListener(
      'drop',
      event => {
        event.preventDefault();
        event.stopPropagation();
        this.log('Drop event stopped');
      },
      { capture: true, signal: this.detachedController?.signal }
    );

    // Field events
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'click',
      this.handleAnnotationClick,
      { signal: this.detachedController?.signal }
    );
    viewerApplication.appConfig.viewerContainer.addEventListener(
      'focus',
      this.handleAnnotationFocus,
      {
        capture: true,
        signal: this.detachedController?.signal
      }
    );

    this.iframe.contentWindow?.window.addEventListener(
      'keydown',
      this.handleKeydown,
      {
        capture: true,
        signal: this.detachedController?.signal
      }
    );

    this.viewerApplication.eventBus.on('annotationeditormodechanged', () => {
      this.log(
        'Annotation editor mode changed',
        this.viewerApplication!.pdfViewer.annotationEditorMode
      );
      this.onAnnotationEditorModeChanged?.(this.viewerApplication!);
    });

    const firstPagePromise = viewerApplication.pdfViewer.firstPagePromise;
    if (!firstPagePromise) {
      throw new Error('Expected PDF viewer firstPagePromise');
    }
    await firstPagePromise;
    this.log('First page loaded');
    const pagesPromise = viewerApplication.pdfViewer.pagesPromise;
    if (!pagesPromise) {
      throw new Error('Expected PDF viewer pagesPromise');
    }
    await pagesPromise;
    this.log('All pages loaded');

    this.pdfLoaded.resolve(viewerApplication);
    this.onPdfLoaded?.(viewerApplication);
  }

  isAnnotationField(
    target: EventTarget | null
  ): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
    return (
      target &&
      'matches' in target &&
      typeof target.matches === 'function' &&
      target.matches(annotationFieldSelector)
    );
  }

  handleAnnotationClick = async (event: Event) => {
    this.log('Annotation clicked');
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }

    // Make radios uncheckable.
    if (
      target.type === 'radio' &&
      target instanceof this.iframe.contentWindow!.window.HTMLInputElement &&
      target.checked
    ) {
      // Click happens before the change event.
      // Wait for the change event to fire, if it does, we do not need to clear the radio because it just became checked.
      const timeout = setTimeout(() => {
        target.checked = false;
        dispatchChange(target);
      });
      target.addEventListener('change', () => clearTimeout(timeout), {
        once: true,
        capture: true
      });
    }

    this.onAnnotationFieldClick?.(this.viewerApplication, target, event);
  };

  handleAnnotationFocus = async (event: Event) => {
    this.log('Annotation focused');
    const target = event.target;
    if (!this.isAnnotationField(target) || !this.viewerApplication) {
      return;
    }
    this.onAnnotationFieldFocus?.(this.viewerApplication, target, event);
  };

  handleKeydown = (event: KeyboardEvent) => {
    if (
      !(event.ctrlKey || event.metaKey) ||
      event.shiftKey ||
      event.altKey ||
      event.key !== 's' ||
      event.defaultPrevented ||
      !this.viewerApplication
    ) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation(); // Prevents the default save dialog from opening.
    const target = event.target;
    if (this.isAnnotationField(target)) {
      target.blur();
      target.focus();
    }
    this.onCtrlS?.(this.viewerApplication);
  };

  log(...data: any[]) {
    console.log(`PDF_VIEWER_${this.instance}`, ...data);
  }
}
